import { useMemo, useState, type KeyboardEvent } from 'react';
import { useCrmLeadsHidden } from './crm-leads-hidden';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from '@gokrew/ui/components/command';
import {
  Building2,
  Columns3,
  LayoutDashboard,
  ListTodo,
  Mail,
  Plus,
  UserPlus,
  UsersRound,
  Settings,
  Target,
  type LucideIcon,
  Presentation,
} from 'lucide-react';
import { useCrmFormat } from './crm-format';
import { useCrmDialogs } from './crm-dialogs';
import styles from './Crm.module.css';

type PaletteAction = {
  id: string;
  shortcut: string;
  label: string;
  Icon: LucideIcon;
  run: () => void;
};

export const isMac = () =>
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad/.test(navigator.userAgent);

/**
 * Cmd+K (Ctrl+K) quick actions. With the search box empty, a single key
 * runs its action straight away: Cmd+K then D opens a new opportunity.
 */
export const CrmCommandPalette = ({
  open,
  onOpenChange,
  onSettings,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSettings: () => void;
}) => {
  const { t } = useCrmFormat();
  const leadsHidden = useCrmLeadsHidden();
  const navigate = useNavigate();
  const dialogs = useCrmDialogs();
  const [search, setSearch] = useState('');
  const groups = useMemo(() => {
    const go = (path: string) => () => navigate(path);
    const create: PaletteAction[] = [
      {
        id: 'deal',
        shortcut: 'D',
        label: t('crm.workspace.new_deal'),
        Icon: Plus,
        run: () => dialogs.newDeal(),
      },
      {
        id: 'company',
        shortcut: 'E',
        label: t('crm.add_company'),
        Icon: Building2,
        run: dialogs.newCompany,
      },
      {
        id: 'contact',
        shortcut: 'C',
        label: t('crm.add_contact'),
        Icon: UserPlus,
        run: dialogs.newContact,
      },
      {
        id: 'activity',
        shortcut: 'A',
        label: t('crm.workspace.add_activity'),
        Icon: ListTodo,
        run: dialogs.newActivity,
      },
    ];
    const pages: PaletteAction[] = [
      ['dashboard', '/crm', LayoutDashboard],
      ['leads', '/crm/leads', Target],
      ['deals', '/crm/deals', Columns3],
      ['companies', '/crm/companies', Building2],
      ['contacts', '/crm/contacts', UsersRound],
      ['activities', '/crm/activities', ListTodo],
      ['inbox', '/crm/inbox', Mail],
      ['demos', '/crm/demos', Presentation],
    ]
      .filter(([key]) => key !== 'leads' || !leadsHidden)
      .map(([key, path, Icon], index) => ({
        id: key as string,
        shortcut: String(index + 1),
        label: t(`crm.workspace.${key}`),
        Icon: Icon as LucideIcon,
        run: go(path as string),
      }));
    pages.push({
      id: 'settings',
      shortcut: 'S',
      label: t('crm.settings.title'),
      Icon: Settings,
      run: onSettings,
    });
    return { create, pages };
  }, [t, navigate, dialogs, onSettings, leadsHidden]);
  const run = (action: PaletteAction) => {
    onOpenChange(false);
    setSearch('');
    // Let the palette close (and give focus back) before a dialog opens.
    window.setTimeout(action.run, 0);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (
      search ||
      event.metaKey ||
      event.ctrlKey ||
      event.altKey ||
      event.key.length !== 1
    )
      return;
    const action = [...groups.create, ...groups.pages].find(
      (item) => item.shortcut === event.key.toUpperCase()
    );
    if (!action) return;
    event.preventDefault();
    run(action);
  };
  const item = (action: PaletteAction) => (
    <CommandItem
      key={action.id}
      value={`${action.id} ${action.label}`}
      onSelect={() => run(action)}
      className={styles.paletteItem}
    >
      <action.Icon aria-hidden="true" />
      {action.label}
      <CommandShortcut className={styles.paletteKey}>
        {action.shortcut}
      </CommandShortcut>
    </CommandItem>
  );
  return (
    <CommandDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setSearch('');
        onOpenChange(next);
      }}
      title={t('crm.palette.title')}
      description={t('crm.palette.description')}
      className={styles.palette}
      showCloseButton={false}
    >
      <CommandInput
        value={search}
        onValueChange={setSearch}
        onKeyDown={onKeyDown}
        placeholder={t('crm.palette.placeholder')}
      />
      <CommandList>
        <CommandEmpty>{t('crm.no_results')}</CommandEmpty>
        <CommandGroup heading={t('crm.palette.create')}>
          {groups.create.map(item)}
        </CommandGroup>
        <CommandGroup heading={t('crm.palette.go')}>
          {groups.pages.map(item)}
        </CommandGroup>
      </CommandList>
      <p className={styles.paletteHint}>
        {t('crm.palette.hint', { key: isMac() ? '⌘' : 'Ctrl' })}
      </p>
    </CommandDialog>
  );
};
